import { useState, useRef, useCallback, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { ReactComponent as DeleteIcon } from '../../../assets/pipeline/delete.svg';
import { reilmAPI } from '../../../services/apiClient';
import { STAGE_OPTIONS, STRATEGY_OPTIONS } from '../options';
import { CONTACT_ROLE_OPTIONS } from './contactOptions';

const initialForm = {
  stage: 'new_lead',
  strategy: '',
  propertyType: '',
  price: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    coordinates: { lat: null, lon: null },
  },
  details: {
    beds: '',
    baths: '',
    sqft: '',
    lotSize: '',
    yearBuilt: '',
    hoaFee: '',
    sewer: '',
    parking: '',
    amenities: '',
    floodZone: '',
    tax: { amount: '', year: '' },
    schools: {
      elementary: '',
      elementary_state_rate: '',
      middle: '',
      middle_state_rate: '',
      high: '',
      high_state_rate: '',
    },
  },
  contact: [{ name: '', mobile: '', email: '', role: '', other: '' }],
};

// Keys are the provider's codes, values are the only four strings the property model's enum accepts,
// so this map may never emit anything else -- a value outside the four is rejected on save, not shown
// with a warning. The provider's vocabulary is the wider one and it is open-ended: 22 sampled detail
// responses on 11.08 returned `single_family`, `condos`, `apartment`, `industrial` and `other`. Only
// `condos` was a missed translation -- the map had the singular, so condos imported with the type
// blank while every other field filled. The rest have no counterpart among the four and deliberately
// map to nothing, for the user to pick; an unset type is savable for exactly this reason.
const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  multi_family: 'Multi Family',
  condo: 'Condo',
  condos: 'Condo',
  townhouse: 'Townhouse',
};

export function mapApiToForm(d, fallbackLabel) {
  const allSchools = d?.schools?.schools ?? [];
  const find = (level) => allSchools.find(s => s.education_levels?.[0] === level);
  const cleanName = (s, suffix) => s?.name?.replace(new RegExp(suffix, 'i'), '').trim() ?? '';
  const transformDetails = (details) => {
    if (!details?.length) return {};
    return details.reduce((acc, entry) => {
      // A real detail response can carry a null entry, or an entry with no category. Every other
      // read in this function is defensive about a missing field; skipping here instead of throwing
      // keeps one bad entry from losing beds/baths/sqft/price/schools -- everything mapApiToForm
      // produces -- since a throw here propagates out of the whole function before any field is
      // assigned.
      const category = entry?.category;
      if (!category) return acc;
      acc[category.toLowerCase().replace(/\s+/g, '_')] = entry.text;
      return acc;
    }, {});
  }

  return {
    address: {
      street: d?.location?.address?.line ?? fallbackLabel,
      city: d?.location?.address?.city ?? '',
      state: d?.location?.address?.state_code ?? '',
      zip: d?.location?.address?.postal_code ?? '',
      coordinates: {
        lat: d?.location?.address?.coordinate?.lat ?? null,
        lon: d?.location?.address?.coordinate?.lon ?? null,
      },
    },
    propertyType: PROPERTY_TYPE_LABELS[d?.description?.type] ?? '',
    price: d?.list_price ?? '',
    history: d?.property_history?.map( h => {
        return { date: h.date, event_name: h.event_name, price: h.price, mls_source: h.source_name }
    }),
    details: {
      beds: d?.description?.beds ?? '',
      baths: d?.description?.baths ?? '',
      sqft: d?.description?.sqft ?? '',
      lotSize: d?.description?.lot_sqft
        ? (d.description.lot_sqft / 43560).toFixed(2)
        : '',
      yearBuilt: d?.description?.year_built ?? '',
      parking: d?.description?.garage != null ? 'garage' : '',
      hoaFee: d?.hoa?.fee ?? '',
      floodZone: d?.local?.flood?.fema_zone?.[0] ?? '',
      sewer: '',
      amenities: transformDetails(d?.details),
      tax: {
        amount: d?.tax_history?.[0]?.tax ?? '',
        year: d?.tax_history?.[0]?.year ?? '',
      },
      schools: {
        elementary: cleanName(find('elementary'), 'Elementary School'),
        elementary_state_rate: find('elementary')?.rating ?? '',
        middle: cleanName(find('middle'), 'Middle School'),
        middle_state_rate: find('middle')?.rating ?? '',
        high: cleanName(find('high'), 'High School'),
        high_state_rate: find('high')?.rating ?? '',
      },
    },
  };
}

// react-select styles — brand token aligned
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    borderRadius: '8px',
    borderColor: '#DAE4D5',
    boxShadow: 'none',
    fontFamily: 'Figtree, sans-serif',
    fontSize: '13px',
    '&:hover': { 
      borderColor: state.isDisabled ? '#DAE4D5' : '#4CBC18' 
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 12px' }),
  singleValue: (base) => ({ ...base, color: '#17270D', fontFamily: 'Figtree' }),
  placeholder: (base) => ({ ...base, color: '#DAE4D5', fontFamily: 'Figtree', fontSize: '13px', fontWeight: 'bold' }),
  option: (base, state) => ({
    ...base,
    fontFamily: 'Figtree',
    fontSize: '13px',
    paddingLeft: '28px',
    backgroundColor: state.isFocused ? '#EEFBE7' : 'white',
    color: '#17270D',
    '&:active': { backgroundColor: '#EEFBE7' },
  }),
  groupHeading: (base) => ({
    ...base,
    color: '#a7c199',
    fontFamily: 'Figtree',
    fontSize: '12px',
    fontWeight: 600,
    paddingLeft: '12px',
    textTransform: 'none',
  }),
  menu: (base) => ({
    ...base,
    border: '1px solid #7dbd42',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    zIndex: 999,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
};

// Color scale matches assets/arv/school-ratings-color.svg
const getScoreBg = (n) => {
  if (!n || n <= 0) return '#C4CCBE';
  if (n <= 4)       return '#4F7A35';
  if (n <= 7)       return '#2F4A1F';
  return '#17270D';
};

function ScoreBadge({ score }) {
  const num = parseInt(score, 10);
  return (
    <div
      className="flex items-center justify-center w-10 shrink-0 self-stretch rounded-l-lg text-[11px] font-semibold text-white"
      style={{ backgroundColor: getScoreBg(num) }}
    >
      {score ? `${num}/10` : ''}
    </div>
  );
}

function Label({ children, muted }) {
  return (
    <label className={`block text-[13px] mb-[6px] ${muted ? 'text-brand-secondary' : 'text-brand-dark'}`}>
      {children}
    </label>
  );
}

function Field({ label, muted, children }) {
  return (
    <div>
      <Label muted={muted}>{label}</Label>
      {children}
    </div>
  );
}

function SectionHeader({ children }) {
  return <h3 className="text-[15px] text-brand-secondary mb-4 mt-2">{children}</h3>;
}

const inputCls = 'w-full h-10 rounded-md border border-brand-muted bg-white px-3 text-[13px] text-brand-dark placeholder:text-brand-muted outline-none focus:border-brand-hover transition-colors hover:border-brand-hover disabled:border-brand-muted';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function AddOpportunity({ onClose, save }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [addressValue, setAddressValue] = useState(null);
  const [openRoleIdx, setOpenRoleIdx] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // A failed address lookup used to render as "No results", identical to a genuine no-match. That
  // is how a broken lookup reached a review round with the wrong cause attached to it: from the
  // screen there was nothing to tell apart an unreachable API, a rejected request and an address
  // that simply does not exist.
  const [searchFailed, setSearchFailed] = useState(false);
  // Same shape as searchFailed, one call later: the detail lookup used to swallow its failure into a
  // fallback that only set the street, leaving city/state/zip/price/beds/baths/sqft blank with nothing
  // on screen to say why. The backend now retries this call once (route.js isDownstreamFlake) for the
  // known intermittent shape, so this only fires once that retry is also exhausted.
  const [detailFailed, setDetailFailed] = useState(false);
  const debounceTimer = useRef(null);

  const addressFilled = !!form.address.street;

  // Close role dropdown on outside click
  useEffect(() => {
    if (openRoleIdx === null) return;
    const close = () => setOpenRoleIdx(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openRoleIdx]);

  // Scoped setters for nested sub-objects
  const setTop = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const setDetails = (field) => (e) =>
    setForm(prev => ({ ...prev, details: { ...prev.details, [field]: e.target.value } }));

  const setTax = (field) => (e) =>
    setForm(prev => ({
      ...prev,
      details: { ...prev.details, tax: { ...prev.details.tax, [field]: e.target.value } },
    }));

  const setSchool = (field) => (e) =>
    setForm(prev => ({
      ...prev,
      details: {
        ...prev.details,
        schools: { ...prev.details.schools, [field]: e.target.value },
      },
    }));

  const setSelectField = (field) => (option) =>
    setForm(prev => ({ ...prev, [field]: option?.value ?? '' }));

  // The generic setDetails would overwrite the whole details.amenities dict with the typed string,
  // discarding every other category transformDetails put there -- homeowners_association among them,
  // which PropertyDashboard reads back out on the saved property (details.amenities?.homeowners_association).
  // Update just the one key the field edits so a later Finish still carries the rest.
  const setAmenitiesText = (e) => setForm(prev => ({
    ...prev,
    details: {
      ...prev.details,
      amenities: prev.details.amenities && typeof prev.details.amenities === 'object'
        ? { ...prev.details.amenities, amenities_and_community_features: [e.target.value] }
        : e.target.value,
    },
  }));

  // ---------------------------------------------------------------------------
  // Address async select
  // ---------------------------------------------------------------------------
  const loadOptions = useCallback((inputValue, callback) => {
    clearTimeout(debounceTimer.current);
    if (inputValue.length <= 3) { callback([]); return; }
    debounceTimer.current = setTimeout(async () => {
      try {
        const { data } = await reilmAPI.searchAddress(inputValue);
        // RapidAPI's own envelope is { data: {...}, status, message }; our backend re-wraps that
        // as { data: r.data } before it reaches here, so the real payload sits three levels deep.
        const opts = data?.data?.data?.autocomplete
          ?.filter(a => a.area_type === 'address')
          ?.map(a => ({ value: a.id, label: a?.full_address?.[0] })) ?? [];
        setSearchFailed(false);
        callback(opts);
      } catch {
        setSearchFailed(true);
        callback([]);
      }
    }, 800);
  }, []);

  const onAddressChange = async (option) => {
    if (!option) {
      setAddressValue(null);
      setForm(prev => ({ ...prev, address: initialForm.address }));
      setDetailFailed(false);
      return;
    }
    setAddressValue(option);
    try {
      const { data } = await reilmAPI.realtorPropertyDetail(option.value);
      // Same triple-nesting as searchAddress above -- confirmed live 14.08 for both endpoints.
      const mapped = mapApiToForm(data?.data?.data, option.label);
      setForm(prev => ({ ...prev, ...mapped }));
      setDetailFailed(false);
    } catch (err) {
      // Same guard as handleFinish: a 401 here means the interceptor is already redirecting to
      // login, so showing a "details unavailable" message would flash the wrong cause on the way out.
      if (err?.response?.status === 401) return;
      // Fallback: at minimum set street from the selected label. Everything else stays blank, so the
      // message below is what tells the user that's a failed import and not a listing with no data.
      setForm(prev => ({
        ...prev,
        address: { ...prev.address, street: option.label },
      }));
      setDetailFailed(true);
    }
  };

  // ---------------------------------------------------------------------------
  // Collapse toggle and save
  // ---------------------------------------------------------------------------
  const handleCollapseToggle = () => {
    if (!addressFilled) { onClose(); return; }
    handleFinish();
  };

  const handleFinish = async () => {
    setSaveError(null);
    try {
      const { data } = await reilmAPI.savePropertyDetails({ propertyDetails: form });
      if (save) save(data?.data?.property);
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) return; // interceptor already redirecting to login
      setSaveError(
        err?.response?.data?.error?.message
        || 'Failed to save property. Please try again.'
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Contact list
  // ---------------------------------------------------------------------------
  const updateContact = (idx, field, value) => {
    setForm(prev => {
      const list = prev.contact.map((c, i) => i === idx ? { ...c, [field]: value } : c);
      return { ...prev, contact: list };
    });
  };

  const addContact = () => {
    if (form.contact.length >= 3) return;
    setForm(prev => ({
      ...prev,
      contact: [...prev.contact, { name: '', mobile: '', email: '', role: '', other: '' }],
    }));
  };

  const deleteContact = (idx) => {
    setForm(prev => ({ ...prev, contact: prev.contact.filter((_, i) => i !== idx) }));
  };

  const phoneFormatEval = (n) => {
      if ( n === undefined || n==='' ) return null
      if ( n.length === 10 ) {
          const digits = n?.split('')
          const areacode = `(${digits?.at(0)}${digits?.at(1)}${digits?.at(2)}) `
          const phone = `${digits?.at(3)}${digits?.at(4)}${digits?.at(5)}-${digits?.at(6)}${digits?.at(7)}${digits?.at(8)}${digits?.at(9)}`
          
          return areacode + phone
      }
      return n.replace(/\D/g, "")
      
  }

  // ---------------------------------------------------------------------------
  // Derived values for react-select controlled inputs
  // ---------------------------------------------------------------------------
  const stageValue = STAGE_OPTIONS
    .flatMap(g => g.options)
    .find(o => o.value === form.stage) ?? null;

  const strategyValue = STRATEGY_OPTIONS
    .flatMap(g => g.options)
    .find(o => o.value === form.strategy) ?? null;

  // form.details.amenities has two legitimate shapes: the dict transformDetails produces when it
  // comes straight from the API, or a plain string once the user has typed into the field (setDetails
  // overwrites the whole value on every keystroke, it doesn't know about the dict shape). Reading it
  // as the object unconditionally blanked the field the moment someone typed one character.
  const amenitiesValue = typeof form.details.amenities === 'string'
    ? form.details.amenities
    : form.details.amenities?.amenities_and_community_features?.[0] ?? '';

  const handleStepClick = (target) => {
    if (addressFilled) setStep(target);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="fixed top-0 right-0 h-full w-full md:w-[960px] z-[200] flex flex-col bg-brand-pageFill shadow-[-4px_0_24px_rgba(0,0,0,0.08)]">

      {/* Collapse toggle — desktop only. It floats outside the panel's own left edge (in the page
          area exposed to its left), which only exists at md+ where the panel doesn't fill the
          viewport; on mobile the panel is full-width so there's no exposed area to float it in.
          Mobile gets its own inline close button in the stepper row instead. */}
      <button
        onClick={handleCollapseToggle}
        className="hidden md:flex absolute -left-8 top-1/2 -translate-y-1/2 items-center justify-center w-16 h-16 rounded-full bg-white border border-brand-muted text-brand-dark hover:bg-brand-pageFill transition-colors z-10"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M6 4l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Decorative bg */}
      {/* <div className="absolute bottom-0 right-0 w-[540px] h-[560px] pointer-events-none overflow-hidden">
        <svg viewBox="0 0 540 560" fill="none" className="w-full h-full">
          <ellipse cx="420" cy="420" rx="340" ry="300" fill="#EEFBE7" />
          <ellipse cx="490" cy="500" rx="120" ry="110" fill="#DAE4D5" opacity="0.6" />
          <ellipse cx="320" cy="480" rx="220" ry="190" fill="#f8fef4" opacity="0.8" />
        </svg>
      </div> */}

      {/* Stepper */}
      <div className="flex items-center pl-4 pr-4 md:pl-16 md:pr-[8rem] py-8 border-b border-brand-muted/40 z-10 shrink-0">
        <button
          type="button"
          onClick={handleCollapseToggle}
          className="md:hidden mr-3 shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-white border border-brand-muted text-brand-dark"
        >
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
            <path d="M6 4l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${step === 1 ? 'bg-brand-green text-white' : 'border border-brand-muted text-brand-muted'}`}>1</div>
          <span
            onClick={() => handleStepClick(1)}
            className={`text-[15px] font-semibold ${step === 1 ? 'text-brand-dark' : 'text-brand-muted'} ${addressFilled ? 'cursor-pointer' : 'cursor-default'}`}
          >
            Property Details
          </span>
        </div>
        <div className="flex-1 mx-6 h-px bg-brand-disabled/50" />
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold ${step === 2 ? 'bg-brand-green text-white' : 'border border-brand-disabled text-brand-disabled'}`}>2</div>
          <span
            onClick={() => handleStepClick(2)}
            className={`text-[15px] font-semibold ${step === 2 ? 'text-brand-dark' : 'text-brand-disabled'} ${addressFilled ? 'cursor-pointer' : 'cursor-default'}`}
          >
            Contacts
          </span>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 md:px-16 py-6 z-10">

        {/* ----------------------------------------------------------------- */}
        {/* STEP 1 — Property Details                                          */}
        {/* ----------------------------------------------------------------- */}
        {step === 1 && (
          <div className="space-y-5 max-w-[752px]">
            <p className="text-[13px] text-brand-secondary font-medium">Enter a property address to continue.</p>

            <Field label="Address">
              <AsyncSelect
                value={addressValue}
                loadOptions={loadOptions}
                onChange={onAddressChange}
                isClearable
                isMulti={false}
                placeholder="Search address..."
                noOptionsMessage={({ inputValue }) => {
                  if (inputValue.length <= 3) return 'Type 4+ characters to search';
                  return searchFailed ? 'Address lookup unavailable, try again' : 'No results';
                }}
                styles={rsStyles}
                components={{ DropdownIndicator: () => null }}
              />
              {detailFailed && (
                <p className="text-[12px] text-red-400 mt-[6px]">
                  Could not load property details, try again
                </p>
              )}
            </Field>

            {/* Beds / Baths / Sqft / Year Built */}
            {/* 16.08 (Set#5 item 1.1): Stage moved out of this row into Property Type's row below,
                per Cong's request. It sat here as a 5th flex-1 sibling next to four fixed-width
                fields with no responsive variant, so below desktop widths its share of the row went
                negative and the Select rendered crushed. */}
            <div className="flex flex-wrap gap-[19px] items-end">
              <div className="w-14">
                <Label>Beds</Label>
                <input type="text" value={form.details.beds} onChange={setDetails('beds')} className={inputCls} />
              </div>
              <div className="w-14">
                <Label>Baths</Label>
                <input type="text" value={form.details.baths} onChange={setDetails('baths')} className={inputCls} />
              </div>
              <div className="w-[98px]">
                <Label>Sqft</Label>
                <input type="text" value={form.details.sqft} onChange={setDetails('sqft')} className={inputCls} />
              </div>
              <div className="w-[98px]">
                <Label>Year Built</Label>
                <input type="text" value={form.details.yearBuilt} onChange={setDetails('yearBuilt')} className={inputCls} />
              </div>
            </div>

            {/* Property Type + Stage, Strategy on its own row (Set#5 item 1.1) */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Property Type">
                <input type="text" value={form.propertyType} onChange={setTop('propertyType')} className={inputCls} />
              </Field>
              <Field label="Stage">
                <Select
                  value={stageValue}
                  options={STAGE_OPTIONS}
                  onChange={setSelectField('stage')}
                  styles={rsStyles}
                  isSearchable={false}
                />
              </Field>
            </div>
            <Field label="Strategy">
              <Select
                value={strategyValue}
                options={STRATEGY_OPTIONS}
                onChange={setSelectField('strategy')}
                styles={rsStyles}
                isSearchable={false}
              />
            </Field>

            {/* Multi-Family (disabled stub) + Sewer */}
            {/* <div className="grid grid-cols-2 gap-4">
              <Field label="Multi-Family" muted>
                <div className={`${inputCls} bg-brand-grayGreen opacity-50 cursor-not-allowed`} />
              </Field>
              <Field label="Sewer">
                <input type="text" value={form.details.sewer} onChange={setDetails('sewer')} className={inputCls} />
              </Field>
            </div> */}

            {/* Lot Size + Flood Zone */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Lot Size">
                <input type="text" value={form.details.lotSize} onChange={setDetails('lotSize')} className={inputCls} />
              </Field>
              <Field label="Flood Zone">
                <input type="text" value={form.details.floodZone} onChange={setDetails('floodZone')} className={inputCls} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Parking">
                <input type="text" value={form.details.parking} onChange={setDetails('parking')} className={inputCls} />
              </Field>
            </div>

            {/* HOA */}
            <div>
              <SectionHeader>HOA</SectionHeader>
              <div className="grid grid-cols-2 gap-4">
                <Field label="HOA Fee">
                  <div className="flex h-10 rounded-lg border border-brand-muted bg-white overflow-hidden hover:border-brand-green focus-within:border-brand-green transition-colors">
                    <span className="flex items-center pl-3 text-[13px] font-medium text-brand-dark">$</span>
                    <input
                      type="text"
                      value={form.details.hoaFee}
                      onChange={setDetails('hoaFee')}
                      className="flex-1 px-2 text-[13px] text-brand-dark outline-none"
                    />
                    <span className="flex items-center pr-3 text-[12px] text-brand-muted"></span>
                  </div>
                </Field>
                <Field label="Amenities">
                  <input
                    type="text"
                    value={amenitiesValue}
                    onChange={setAmenitiesText}
                    className={inputCls}
                  />
                </Field>
              </div>
            </div>

            {/* Schools */}
            <div>
              <SectionHeader>Schools</SectionHeader>
              <div className="grid grid-cols-3 gap-x-[16px]">
                {[
                  { label: 'Elementary School', nameField: 'elementary', rateField: 'elementary_state_rate' },
                  { label: 'Middle School',      nameField: 'middle',     rateField: 'middle_state_rate'     },
                  { label: 'High School',        nameField: 'high',       rateField: 'high_state_rate'       },
                ].map(({ label, nameField, rateField }) => (
                  <div key={label}>
                    {/* 16.08 (Set#5 item 1.2): "Elementary School" wraps to two lines at the
                        mobile column width, "Middle School"/"High School" don't, so their inputs
                        sat one line higher and the row didn't line up. Reserving two lines' height
                        (measured) fixes it there; at the wider desktop column all three labels
                        already fit on one line, so md:min-h-0 drops the reservation rather than
                        add unrequested whitespace above a row that was already aligned. */}
                    <div className="min-h-[39px] md:min-h-0">
                      <Label>{label}</Label>
                    </div>
                    <div className="flex items-center gap-x-[8px] mt-[6px]">
                      {form.details.schools[rateField] != null && form.details.schools[rateField] !== '' && (
                        <span
                          className="w-[44px] h-[44px] rounded-full flex items-center justify-center text-white font-bold shrink-0"
                          style={{ backgroundColor: getScoreBg(parseInt(form.details.schools[rateField], 10)) }}
                        >
                          <span style={{ fontSize: 15 }}>{form.details.schools[rateField]}</span>
                          <span style={{ fontSize: 10, fontWeight: 400 }}>/10</span>
                        </span>
                      )}
                      <input
                        type="text"
                        value={form.details.schools[nameField]}
                        onChange={setSchool(nameField)}
                        className={inputCls}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Taxes */}
            <div className="pb-6">
              <SectionHeader>Taxes</SectionHeader>
              <div className="grid grid-cols-2 gap-4 w-full md:w-[486px]">
                <Field label="Tax Year">
                  <input type="text" value={form.details.tax.year} onChange={setTax('year')} className={inputCls} />
                </Field>
                <Field label="Total Tax">
                  <input type="text" value={form.details.tax.amount} onChange={setTax('amount')} className={inputCls} />
                </Field>
              </div>
            </div>
          </div>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* STEP 2 — Contacts                                                  */}
        {/* ----------------------------------------------------------------- */}
        {step === 2 && (
          <div className="max-w-[776px] space-y-4">
            <p className="text-[13px] font-bold text-[#a7c199]">Contacts</p>

            {form.contact.map((cont, idx) => (
              <div key={idx} className="space-y-4">
                {/* Contact index + delete */}
                <div className="flex items-center justify-between">
                  <span className="text-[13px] text-[#a7c199]">{idx + 1}</span>
                  {form.contact.length > 1 && (
                    <button
                      type="button"
                      onClick={() => deleteContact(idx)}
                      className="flex items-center justify-center w-4 h-4"
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </div>

                {/* Two-column contact fields */}
                <div className="flex flex-col md:flex-row gap-4 md:gap-[40px] items-start">
                  {/* Left col: Name+Role combo + Email */}
                  <div className="flex flex-col gap-4 w-full md:w-[368px]">
                    {/* Name + Role as one unified field */}
                    <div>
                      <Label>Name</Label>
                      <div className="relative h-[40px] bg-white border border-[#cbdcc3] rounded-[4px] flex items-center justify-between px-[8px]">
                        <input
                          type="text"
                          value={cont.name}
                          onChange={e => updateContact(idx, 'name', e.target.value)}
                          placeholder="John Doe"
                          className="flex-1 text-[13px] text-[#1a2a10] placeholder:text-[#cbdcc3] outline-none bg-transparent"
                        />
                        {/* Role trigger */}
                        <button
                          type="button"
                          onMouseDown={e => {
                            e.stopPropagation();
                            setOpenRoleIdx(openRoleIdx === idx ? null : idx);
                          }}
                          className="flex items-center gap-[4px] shrink-0 ml-2"
                        >
                          <span className="text-[13px] text-[#1a2a10] leading-[24px] whitespace-nowrap">
                            {cont.role || 'Select'}
                          </span>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M4 6l4 4 4-4" stroke="#1a2a10" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>

                        {/* Role dropdown — dark popover */}
                        {openRoleIdx === idx && (
                          <div
                            onMouseDown={e => e.stopPropagation()}
                            className="absolute top-[44px] right-0 z-50 bg-[#17270d] rounded-[8px] p-[14px] flex flex-col gap-[8px] drop-shadow-md min-w-[120px]"
                          >
                            {CONTACT_ROLE_OPTIONS.map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                onMouseDown={e => {
                                  e.stopPropagation();
                                  updateContact(idx, 'role', opt.value);
                                  setOpenRoleIdx(null);
                                }}
                                className={`text-left text-[13px] font-light leading-[12px] text-white whitespace-nowrap px-[4px] py-[4px] rounded-[2px] transition-colors ${
                                  cont.role === opt.value ? 'bg-[#4f6c3c]' : 'hover:bg-[#4f6c3c]/60'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <Label>Email Address</Label>
                      <input
                        type="email"
                        value={cont.email}
                        onChange={e => updateContact(idx, 'email', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                  </div>

                  {/* Right col: Mobile + Other */}
                  <div className="flex flex-col gap-4 w-full md:w-[368px]">
                    <div>
                      <Label>Mobile #</Label>
                      <input
                        type="text"
                        value={ phoneFormatEval(cont.mobile) }
                        onChange={e => updateContact(idx, 'mobile', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label>Other #</Label>
                      <input
                        type="text"
                        value={ phoneFormatEval(cont.other)}
                        onChange={e => updateContact(idx, 'other', e.target.value)}
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Contact pill */}
            {form.contact.length < 3 && (
              <div className="flex items-center w-full pt-2">
                <div className="flex-1 border-b border-[#cbdcc3]" />
                <button
                  type="button"
                  onClick={addContact}
                  className="flex items-center gap-[6px] border border-[#cbdcc3] rounded-[48px] pl-[12px] pr-[16px] py-[8px] bg-white text-[12px] text-[#17270d] leading-[14px] shrink-0 hover:bg-brand-pageFill transition-colors mx-1"
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M4 1v6M1 4h6" stroke="#17270d" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  Add Contact
                </button>
                <div className="flex-1 border-b border-[#cbdcc3]" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end items-center gap-3 pl-4 pr-4 md:pl-16 md:pr-[8rem] py-5  z-10 shrink-0 bg-brand-pageFill">
        {saveError && (
          <p className="text-[12px] text-red-400 mr-auto">{saveError}</p>
        )}
        {step === 2 && (
          <button
            onClick={() => setStep(1)}
            className="flex items-center gap-2 px-8 h-14 rounded-full border border-brand-muted text-brand-dark text-[15px] hover:bg-brand-pageFill transition-colors"
          >
            <svg width="17" height="12" viewBox="0 0 17 12" fill="none">
              <path d="M16 6H1M1 6l5-5M1 6l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
        )}
        {step === 1 && (
          <button
            onClick={() => setStep(2)}
            disabled={!addressFilled}
            className={`flex items-center gap-2 px-8 h-14 rounded-full border text-[15px] font-medium transition-colors ${
              addressFilled
                ? 'border-brand-green text-brand-green hover:bg-brand-pageFill'
                : 'border-brand-muted text-brand-muted cursor-not-allowed'
            }`}
          >
            Next
            <svg width="17" height="12" viewBox="0 0 17 12" fill="none">
              <path d="M1 6h15M16 6l-5-5M16 6l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <button
          onClick={addressFilled ? handleFinish : undefined}
          disabled={!addressFilled }
          className={`px-8 h-14 rounded-full text-[15px] font-medium transition-colors ${
            addressFilled
              ? 'bg-brand-green text-white hover:bg-[#3da814]'
              : 'bg-[#C9D8C4] text-white cursor-not-allowed'
          }`}
        >
          Finish
        </button>
      </div>
    </div>
  );
}

export default AddOpportunity;
